// biome-ignore-all lint/a11y/noNoninteractiveTabindex: tabIndex enables keyboard scrolling for overflow containers
import { cn } from '@tempo-io/sui-class-utils';
import { FolderMap, VisibilityOff } from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LegendIcon } from './LegendIcon';
import type { LegendItem } from './types';

interface LegendProps {
  items: LegendItem[];
  triggerOnClick?: boolean;
  contentClassName?: string;
}

export function Legend({ items, triggerOnClick = false, contentClassName }: LegendProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const checkOverflow = useCallback(() => {
    const el = sectionRef.current;
    if (!el) return;
    setCanScroll(el.scrollHeight > el.clientHeight);
  }, []);

  if (items.length === 0) {
    return null;
  }

  return (
    <div data-export-target="legend">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Legend"
            className="flex items-center justify-center w-10 h-10 bg-bg-brand hover:bg-bg-brand-hovered rounded-full shadow-lg cursor-pointer transition-colors"
            {...(triggerOnClick
              ? { onClick: () => setOpen((prev) => !prev) }
              : { onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false) })}
          >
            <FolderMap size={20} className="text-text-neutral-inverted" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="end"
          sideOffset={8}
          className={cn(
            'w-57 p-4 bg-bg-surface-base/80 backdrop-blur-xs border border-border-neutral-subtle',
            contentClassName,
          )}
          {...(!triggerOnClick && {
            onMouseEnter: () => setOpen(true),
            onMouseLeave: () => setOpen(false),
          })}
        >
          <h3
            id="legend-heading"
            data-testid="legend-title"
            className="text-sm font-semibold text-text-neutral-primary mb-3"
          >
            {t(($) => $.adaptivePlanner.scenario.legend.title)}
          </h3>
          <div className="relative">
            <section
              ref={(el) => {
                sectionRef.current = el;
                if (el) checkOverflow();
              }}
              className="space-y-2 max-h-64 overflow-y-auto overscroll-contain outline-none"
              aria-labelledby="legend-heading"
              tabIndex={0}
            >
              {items.map((item, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: array items have no stable unique id; index is acceptable here
                  key={`${item.label}-${item.color}-${index}`}
                  data-testid={`legend-color-item-${index}`}
                  className="flex items-center gap-3"
                >
                  <LegendIcon item={item} index={index} />
                  <span className="text-sm text-text-neutral-secondary">{item.label}</span>
                  {item.isHidden && (
                    <VisibilityOff
                      size={14}
                      className="shrink-0 text-text-neutral-secondary"
                      aria-label={t(($) => $.loopVisualization.layout.colorLegend.hiddenHeader)}
                    />
                  )}
                </div>
              ))}
            </section>
            {canScroll && (
              <div
                className="absolute bottom-0 left-0 right-0 h-6 bg-linear-to-t from-bg-surface-base/80 to-transparent pointer-events-none"
                aria-hidden="true"
              />
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
